import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useRestClient } from "@/lib/restClient";
import type { Tenant } from "@/types";

export interface TenantInput {
  tenant_label: string;
  customer: string;
  tenant_region: string;
  settings?: Record<string, unknown>;
}

/**
 * Tenant administration, the same operations as the provisioning command:
 * create a tenant, change it, and attach an account to it.
 */
export function useTenantAdmin() {
  const apiFetch = useRestClient();
  const client = useQueryClient();
  const refresh = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: ["tenants"], refetchType: "all" }),
      client.invalidateQueries({ queryKey: ["accounts"] }),
      client.invalidateQueries({ queryKey: ["account"] }),
      client.invalidateQueries({ queryKey: ["access-scopes"] }),
    ]);

  const create = useMutation({
    // The API assigns the id and returns the created tenant.
    mutationFn: (input: TenantInput) =>
      apiFetch<{ tenant_id: number }>("/api/tenants", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: async (created, input) => {
      const tenant: Tenant = { ...input, tenant_id: String(created.tenant_id) };
      client.setQueryData<Tenant[]>(["tenants"], (current) =>
        current
          ? [...current.filter((entry) => entry.tenant_id !== tenant.tenant_id), tenant]
          : undefined,
      );
      await refresh();
    },
  });
  const update = useMutation({
    mutationFn: ({ tenantId, ...input }: TenantInput & { tenantId: string }) =>
      apiFetch(`/api/tenants/${encodeURIComponent(tenantId)}`, {
        method: "PUT",
        body: JSON.stringify(input),
      }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (tenantId: string) =>
      apiFetch<{ tenant_id: number; soft_deleted: boolean }>(
        `/api/tenants/${encodeURIComponent(tenantId)}`,
        { method: "DELETE" },
      ),
    onSuccess: async (_result, tenantId) => {
      client.setQueryData<Tenant[]>(["tenants"], (current) =>
        current?.filter((tenant) => tenant.tenant_id !== tenantId),
      );
      await refresh();
    },
  });
  const membership = useMutation({
    mutationFn: ({
      tenantId,
      accountIds,
      attached,
    }: {
      tenantId: string;
      accountIds: string[];
      attached: boolean;
    }) =>
      apiFetch<{ updated: number }>(`/api/tenants/${encodeURIComponent(tenantId)}/accounts`, {
        method: "PUT",
        body: JSON.stringify({ account_ids: accountIds, attached }),
      }),
    onSuccess: refresh,
  });
  /** The same accounts added to several tenants, one atomic request per tenant. */
  const memberships = useMutation({
    mutationFn: async ({
      tenantIds,
      accountIds,
    }: {
      tenantIds: string[];
      accountIds: string[];
    }) => {
      for (const tenantId of tenantIds)
        await apiFetch(`/api/tenants/${encodeURIComponent(tenantId)}/accounts`, {
          method: "PUT",
          body: JSON.stringify({ account_ids: accountIds, attached: true }),
        });
      return { tenants: tenantIds.length, accounts: accountIds.length };
    },
    onSettled: refresh,
  });
  return { create, update, remove, membership, memberships };
}

export function tenantDeletionNotice(softDeleted: boolean): string {
  return softDeleted
    ? "Tenant deleted. Existing data and relationships have been retained."
    : "Tenant permanently deleted.";
}

/** The API's message without the transport prefix ("API 409: …"). */
export function apiErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  return message.replace(/^API \d+: /, "");
}
